import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/a1db2c25-97e2-4ceb-a4ec-c06941f029b5/a3ac0cdf-0792-4180-b72e-0df4bd1c7c5e_FMA-CREW-LOGO.png?v=b37410eb2aa41c47106263f8d4f50ddf';
const NEWSLETTER_URL = 'https://readdy.ai/api/form/d6vhmbfb245v7h34ooog';
const PHONE = '470-222-5777';
const PHONE_HREF = 'tel:4702225777';
const EMAIL = 'office@fmacrew.com';

const SERVICES_HREFS = ['#services', '#services', '#services', '#services'];
const COMPANY_HREFS = ['#about', '#why', '#contact', '#contact'];

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const SERVICES_LINKS = [
    { label: t('footer.svc1'), href: SERVICES_HREFS[0] },
    { label: t('footer.svc2'), href: SERVICES_HREFS[1] },
    { label: t('footer.svc3'), href: SERVICES_HREFS[2] },
    { label: t('footer.svc4'), href: SERVICES_HREFS[3] },
  ];

  const COMPANY_LINKS = [
    { label: t('footer.link1'), href: COMPANY_HREFS[0] },
    { label: t('footer.link2'), href: COMPANY_HREFS[1] },
    { label: t('footer.link3'), href: COMPANY_HREFS[2] },
    { label: t('footer.link4'), href: COMPANY_HREFS[3] },
  ];

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubStatus('loading');
    const body = new URLSearchParams({ email });
    try {
      await fetch(NEWSLETTER_URL, { method: 'POST', body, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      setSubStatus('success');
      setEmail('');
    } catch {
      setSubStatus('error');
    }
  };

  return (
    <footer className="bg-[#EBF6F8] pt-12 md:pt-16 pb-8 border-t border-[#B8E2E8]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 pb-10 md:pb-12 border-b border-slate-200">
          <div className="col-span-2 md:col-span-1">
            <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-block mb-4 md:mb-5 cursor-pointer">
              <img src={LOGO_URL} alt="FMA Crew - Facility Maintenance of America" className="h-10 md:h-12 w-auto object-contain" />
            </a>
            <p className="text-slate-500 text-sm leading-relaxed mb-4 md:mb-5">{t('footer.tagline')}</p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-facebook-box-fill', href: 'https://facebook.com', label: 'Facebook' },
                { icon: 'ri-instagram-line', href: 'https://instagram.com', label: 'Instagram' },
                { icon: 'ri-linkedin-box-fill', href: 'https://linkedin.com', label: 'LinkedIn' },
              ].map((s) => (
                <a key={s.label} href={s.href} rel="nofollow noopener noreferrer" target="_blank" aria-label={s.label}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-[#05294A] hover:text-white hover:border-[#05294A] transition-all duration-300 cursor-pointer">
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-slate-900 font-bold text-sm mb-4 md:mb-5">{t('footer.svcHeading')}</p>
            <ul className="flex flex-col gap-2.5 md:gap-3">
              {SERVICES_LINKS.map((link) => (
                <li key={link.href + link.label}>
                  <a href={link.href} onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                    className="text-slate-500 text-sm hover:text-[#05294A] transition-colors cursor-pointer flex items-center gap-2">
                    <i className="ri-arrow-right-s-line text-[#92D7E8]" />{link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-slate-900 font-bold text-sm mb-4 md:mb-5">{t('footer.compHeading')}</p>
            <ul className="flex flex-col gap-2.5 md:gap-3">
              {COMPANY_LINKS.map((link) => (
                <li key={link.href + link.label}>
                  <a href={link.href} onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                    className="text-slate-500 text-sm hover:text-[#05294A] transition-colors cursor-pointer flex items-center gap-2">
                    <i className="ri-arrow-right-s-line text-[#92D7E8]" />{link.label}
                  </a>
                </li>
              ))}
              <li>
                <div className="mt-2 flex flex-col gap-2">
                  <a href={PHONE_HREF} className="text-slate-500 text-sm hover:text-[#05294A] transition-colors cursor-pointer flex items-center gap-2">
                    <i className="ri-phone-line text-[#92D7E8]" />{PHONE}
                  </a>
                  <a href={`mailto:${EMAIL}`} className="text-slate-500 text-sm hover:text-[#05294A] transition-colors cursor-pointer flex items-center gap-2">
                    <i className="ri-mail-line text-[#92D7E8]" />{EMAIL}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <p className="text-slate-900 font-bold text-sm mb-2">{t('footer.newsletter.title')}</p>
            <p className="text-slate-500 text-sm mb-4 md:mb-5 leading-relaxed">{t('footer.newsletter.desc')}</p>
            <form data-readdy-form id="newsletter-form" onSubmit={handleNewsletter} className="flex flex-col gap-3">
              <input type="email" name="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.newsletter.placeholder')}
                className="w-full bg-white border border-slate-200 text-slate-800 placeholder-slate-400 rounded-none px-4 py-2.5 text-sm focus:outline-none focus:border-[#05294A] transition-colors" />
              <button type="submit" disabled={subStatus === 'loading'}
                className="w-full py-2.5 bg-[#05294A] text-white rounded-lg text-sm font-semibold hover:bg-[#032240] transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                {subStatus === 'loading' ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <><span>{t('footer.newsletter.btn')}</span><i className="ri-arrow-right-line" /></>
                )}
              </button>
              {subStatus === 'success' && (
                <p className="text-[#05294A] text-xs flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-fill" /> {t('footer.newsletter.success')}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="pt-6 md:pt-8 border-t border-slate-200">
          {/* Brochure Buttons */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <button
              type="button"
              onClick={() => window.open('/sales/brochure-fma', '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#05294A] text-white text-xs font-semibold rounded-md hover:bg-[#0D4A7A] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-text-line" />
              </div>
              Brochure — FMA
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-external-link-line text-[10px]" />
              </div>
            </button>
            <button
              type="button"
              onClick={() => window.open('/sales/ad01', '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#05294A] text-white text-xs font-semibold rounded-md hover:bg-[#0D4A7A] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-text-line" />
              </div>
              Introduction — AD01
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-external-link-line text-[10px]" />
              </div>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-slate-400 text-xs text-center sm:text-left">{t('footer.copy')}</p>
            <div className="flex items-center gap-3 md:gap-4">
              <span className="text-slate-400 text-xs">Norcross, GA 30093</span>
              <span className="text-slate-200">|</span>
              <a href={PHONE_HREF} className="text-slate-400 text-xs hover:text-[#05294A] transition-colors cursor-pointer">{PHONE}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
